import React, { FC, useState } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Button } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import ClearIcon from '@mui/icons-material/Clear';
import { FormField, TargetRecordingOptions, Shot, TrainingTeamAssignee } from '../../models/training.models';
import { palette } from '../../theme';

interface TargetRecordingFieldComponentProps {
  field: FormField;
  value: Record<number, Shot[]>; // memberId -> shots array
  onChange: (value: Record<number, Shot[]>) => void;
  disabled?: boolean;
  teamMembers: TrainingTeamAssignee[];
}

const TargetRecordingFieldComponent: FC<TargetRecordingFieldComponentProps> = ({
  field,
  value,
  onChange,
  disabled = false,
  teamMembers,
}) => {
  const [selectedAthleteIndex, setSelectedAthleteIndex] = useState(0);

  const options = field.options as TargetRecordingOptions;
  const numShots = options?.num_shots || 10;

  const selectedAthlete = teamMembers[selectedAthleteIndex];
  const athleteShots = selectedAthlete ? value[selectedAthlete.memberId] || [] : [];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedAthleteIndex(newValue);
  };

  const handleTargetClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (disabled || !selectedAthlete) return;

    // Check if max shots reached
    if (athleteShots.length >= numShots) {
      alert(`Maximum ${numShots} shots allowed`);
      return;
    }

    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    const newShot: Shot = {
      x,
      y,
      sequence: athleteShots.length + 1,
      timestamp: new Date().toISOString(),
    };

    const updatedValue = {
      ...value,
      [selectedAthlete.memberId]: [...athleteShots, newShot],
    };

    onChange(updatedValue);
  };

  const handleUndoShot = () => {
    if (disabled || !selectedAthlete || athleteShots.length === 0) return;

    const updatedShots = athleteShots.slice(0, -1);
    const updatedValue = {
      ...value,
      [selectedAthlete.memberId]: updatedShots,
    };

    onChange(updatedValue);
  };

  const handleClearAll = () => {
    if (disabled || !selectedAthlete) return;

    const updatedValue = {
      ...value,
      [selectedAthlete.memberId]: [],
    };

    onChange(updatedValue);
  };

  const renderTarget = () => {
    const targetSize = 400;
    const centerX = targetSize / 2;
    const centerY = targetSize / 2;

    return (
      <svg
        width={targetSize}
        height={targetSize}
        style={{
          cursor: disabled ? 'default' : 'crosshair',
        }}
        onClick={handleTargetClick}
      >
        {/* Black background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={200}
          fill={palette.text.primary}
          stroke={palette.background.paper}
          strokeWidth={2}
        />

        {/* Inner dashed circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={75}
          fill="none"
          stroke={palette.background.paper}
          strokeWidth={1.5}
          strokeDasharray="8 4"
        />

        {/* Inner solid circle */}
        <circle cx={centerX} cy={centerY} r={80} fill="none" stroke={palette.background.paper} strokeWidth={1.5} />

        {/* Outer dashed circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={190}
          fill="none"
          stroke={palette.background.paper}
          strokeWidth={1.5}
          strokeDasharray="8 4"
        />

        {/* Render shot markers */}
        {athleteShots.map((shot, index) => {
          const shotX = shot.x * targetSize;
          const shotY = shot.y * targetSize;

          return (
            <g key={index}>
              {/* Shot marker circle */}
              <circle
                cx={shotX}
                cy={shotY}
                r={8}
                fill={palette.error.main}
                stroke={palette.background.paper}
                strokeWidth={2}
              />
              {/* Sequence number */}
              <text
                x={shotX}
                y={shotY}
                textAnchor="middle"
                dominantBaseline="central"
                fill={palette.background.paper}
                fontSize="10"
                fontWeight="bold"
              >
                {shot.sequence}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  if (teamMembers.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {field.name}
        </Typography>
        <Typography color="text.secondary">No athletes assigned to this training.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {field.name}
      </Typography>

      {/* Athlete Tabs */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={selectedAthleteIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          {teamMembers.map((member, index) => (
            <Tab
              key={member.memberId}
              label={`${member.name} (${value[member.memberId]?.length || 0}/${numShots})`}
              id={`athlete-tab-${index}`}
              aria-controls={`athlete-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Target Area */}
      <Paper variant="outlined" sx={{ p: 3, bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {/* Shot Counter */}
          <Typography
            variant="body1"
            fontWeight={600}
            color={athleteShots.length >= numShots ? 'success.main' : 'text.primary'}
          >
            Shot {athleteShots.length} of {numShots}
          </Typography>

          {/* Target Visualization */}
          {renderTarget()}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<UndoIcon />}
              onClick={handleUndoShot}
              disabled={disabled || athleteShots.length === 0}
              sx={{ textTransform: 'none' }}
            >
              Undo Last Shot
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearAll}
              disabled={disabled || athleteShots.length === 0}
              sx={{ textTransform: 'none' }}
            >
              Clear All
            </Button>
          </Box>

          {/* Instructions */}
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {disabled
              ? 'Form is locked'
              : athleteShots.length >= numShots
              ? `All ${numShots} shots recorded for ${selectedAthlete?.name}`
              : `Click on the target to record shot ${athleteShots.length + 1} for ${selectedAthlete?.name}`}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default TargetRecordingFieldComponent;
